/**
 * 表格配置 Hook
 *
 * 为 ProTable 生成标准化的配置:
 * - 工具栏按钮 (新建、导出、回收站)
 * - 行操作 (查看、编辑、删除)
 * - 分页配置
 */

import { Button, Popconfirm, Space, Typography } from 'antd';
import type React from 'react';
import { useMemo } from 'react';
import { TABLE_DEFAULTS } from '../constants/ui';

// ===== 模块级常量 (避免每次渲染创建新引用) =====

const EMPTY_ROW_ACTIONS: never[] = [];
const EMPTY_TOOLBAR: never[] = [];
/** 从 TABLE_DEFAULTS 派生的可变数组（ProTable 需要 number[] 而非 readonly） */
const DEFAULT_PAGE_SIZE_OPTIONS: number[] = [
  ...TABLE_DEFAULTS.PAGE_SIZE_OPTIONS,
];

function resolveFlag<T>(
  value: boolean | ((record: T) => boolean) | undefined,
  record: T,
): boolean {
  return typeof value === 'function' ? value(record) : Boolean(value);
}

// ===== 类型定义 =====

/**
 * 行操作项
 */
export interface RowAction<T = Record<string, unknown>> {
  /** 操作 key */
  key: string;
  /** 显示文本 */
  label: React.ReactNode;
  /** 点击回调 */
  onClick?: (record: T) => void;
  /** 是否危险操作 (红色) */
  danger?: boolean;
  /** 是否需要确认弹窗 */
  confirm?: {
    title: string;
    content?: React.ReactNode | ((record: T) => React.ReactNode);
  };
  /** 确认后的回调 */
  onConfirm?: (record: T) => void | Promise<void>;
  /** 是否隐藏 */
  hidden?: boolean | ((record: T) => boolean);
  /** 是否禁用 */
  disabled?: boolean | ((record: T) => boolean);
}

/**
 * 工具栏按钮
 */
export interface ToolbarAction {
  /** 按钮 key */
  key: string;
  /** 显示文本 */
  text: string;
  /** 按钮类型 */
  type?: 'primary' | 'default' | 'dashed' | 'link' | 'text';
  /** 点击回调 */
  onClick: () => void;
  /** 图标 */
  icon?: React.ReactNode;
  /** 是否隐藏 */
  hidden?: boolean;
}

/**
 * 表格配置文案（支持国际化）
 */
export interface TableConfigMessages {
  /** 操作列标题，默认 'Actions' */
  actionColumnTitle: string;
  /** 总数展示函数，默认 (total) => `Total ${total}` */
  showTotal: (total: number) => string;
}

const DEFAULT_TABLE_CONFIG_MESSAGES: TableConfigMessages = {
  actionColumnTitle: 'Actions',
  showTotal: (total: number) => `Total ${total}`,
};

/**
 * useTableConfig 配置
 */
export interface UseTableConfigOptions<T = Record<string, unknown>> {
  /** 行操作配置 */
  rowActions?: ReadonlyArray<RowAction<T>>;
  /** 工具栏按钮 */
  toolbar?: ReadonlyArray<ToolbarAction>;
  /** 默认每页条数, 默认 10 */
  defaultPageSize?: number;
  /** 可选每页条数, 默认 [10, 20, 50, 100] */
  pageSizeOptions?: number[];
  /** 表格行 key 字段, 默认 'id' */
  rowKey?: string;
  /** 表格滚动宽度, 默认 'max-content' */
  scrollX?: number | string;
  /** 操作列宽度 */
  actionColumnWidth?: number;
  /** 操作列固定, 默认 'right' */
  actionColumnFixed?: 'left' | 'right' | false;
  /** 自定义文案（支持国际化） */
  messages?: Partial<TableConfigMessages>;
  /** 确认弹窗函数 (需外部注入，未提供时使用 Popconfirm) */
  showConfirm?: (options: {
    title: string;
    content?: React.ReactNode;
    onOk: () => Promise<void> | void;
  }) => void;
}

/**
 * useTableConfig 返回值
 */
export interface UseTableConfigReturn<T = Record<string, unknown>> {
  /** 传给 ProTable 的 pagination 配置 */
  pagination: {
    pageSize: number;
    pageSizeOptions: number[];
    showSizeChanger: boolean;
    showTotal: (total: number) => string;
  };
  /** 表格属性 */
  tableProps: {
    rowKey: string;
    scroll: { x: number | string };
  };
  /** 工具栏渲染 — 传给 ProTable toolBarRender */
  toolBarRender: () => React.ReactNode[];
  /** 操作列配置 — 作为 columns 最后一列 */
  actionColumn: {
    title: string;
    dataIndex: string;
    key: string;
    width?: number;
    fixed?: 'left' | 'right';
    render: (_: unknown, record: T) => React.ReactNode;
  };
  /** 生成行操作渲染 (给定 record) */
  renderRowActions: (record: T) => React.ReactNode;
}

// ===== Hook 实现 =====

/**
 * 表格配置 Hook
 *
 * @example
 * ```tsx
 * const {
 *   pagination,
 *   tableProps,
 *   toolBarRender,
 *   actionColumn,
 * } = useTableConfig<User>({
 *   toolbar: [
 *     { key: 'create', text: '新增', type: 'primary', onClick: () => openCreate() },
 *     { key: 'export', text: '导出', onClick: () => handleExport() },
 *   ],
 *   rowActions: [
 *     { key: 'view', label: '查看', onClick: (r) => goDetail(r.id) },
 *     { key: 'edit', label: '编辑', onClick: (r) => openEdit(r) },
 *     {
 *       key: 'delete', label: '删除', danger: true,
 *       confirm: { title: '确认删除', content: (r) => `确定要删除 ${r.name} 吗？` },
 *       onConfirm: (r) => handleDelete(r.id),
 *     },
 *   ],
 *   showConfirm: ({ title, content, onOk }) => Modal.confirm({ title, content, onOk }),
 * });
 *
 * const columns = [
 *   { title: '姓名', dataIndex: 'name' },
 *   { title: '邮箱', dataIndex: 'email' },
 *   actionColumn, // 操作列
 * ];
 *
 * <ProTable
 *   {...tableProps}
 *   columns={columns}
 *   pagination={pagination}
 *   toolBarRender={toolBarRender}
 * />
 * ```
 */
export function useTableConfig<
  T extends Record<string, unknown> = Record<string, unknown>,
>(options: UseTableConfigOptions<T> = {}): UseTableConfigReturn<T> {
  const {
    defaultPageSize = 10,
    rowKey = 'id',
    scrollX = 'max-content',
    actionColumnWidth,
    actionColumnFixed = 'right',
    showConfirm,
  } = options;

  const msg: TableConfigMessages = {
    ...DEFAULT_TABLE_CONFIG_MESSAGES,
    ...options.messages,
  };

  const rowActions = options.rowActions ?? EMPTY_ROW_ACTIONS;
  const toolbar = options.toolbar ?? EMPTY_TOOLBAR;
  const pageSizeOptions = options.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;

  // 分页
  const pagination = useMemo(
    () => ({
      pageSize: defaultPageSize,
      pageSizeOptions,
      showSizeChanger: true,
      showTotal: msg.showTotal,
    }),
    [defaultPageSize, pageSizeOptions, msg.showTotal],
  );

  // 表格属性
  const tableProps = useMemo(
    () => ({
      rowKey,
      scroll: { x: scrollX },
    }),
    [rowKey, scrollX],
  );

  // 工具栏 — 使用 antd Button
  const toolBarRender = useMemo(
    () => () =>
      toolbar
        .filter((btn) => !btn.hidden)
        .map((btn) => (
          <Button
            key={btn.key}
            type={btn.type}
            icon={btn.icon}
            onClick={btn.onClick}
          >
            {btn.text}
          </Button>
        )),
    [toolbar],
  );

  // 行操作渲染 — 使用 antd Space + Typography.Link + Popconfirm
  const renderRowActions = useMemo(
    () => (record: T) => {
      const visibleActions = rowActions.filter((action) => {
        return !resolveFlag(action.hidden, record);
      });

      return (
        <Space size={4}>
          {visibleActions.map((action) => {
            const isDisabled = resolveFlag(action.disabled, record);
            const confirmConfig = action.confirm;
            const onConfirm = action.onConfirm;

            // 带确认弹窗的操作
            if (confirmConfig && onConfirm) {
              const content =
                typeof confirmConfig.content === 'function'
                  ? confirmConfig.content(record)
                  : confirmConfig.content;

              // 优先使用 antd Popconfirm，提供原生体验
              if (showConfirm) {
                return (
                  <Typography.Link
                    key={action.key}
                    type={action.danger ? 'danger' : undefined}
                    disabled={isDisabled}
                    onClick={() => {
                      if (isDisabled) return;
                      showConfirm({
                        title: confirmConfig.title,
                        content,
                        onOk: () => Promise.resolve(onConfirm(record)),
                      });
                    }}
                  >
                    {action.label}
                  </Typography.Link>
                );
              }

              // 未注入 showConfirm 时，使用 Popconfirm 组件
              return (
                <Popconfirm
                  key={action.key}
                  title={confirmConfig.title}
                  description={content}
                  onConfirm={() => Promise.resolve(onConfirm(record))}
                  disabled={isDisabled}
                >
                  <Typography.Link
                    type={action.danger ? 'danger' : undefined}
                    disabled={isDisabled}
                  >
                    {action.label}
                  </Typography.Link>
                </Popconfirm>
              );
            }

            // 普通操作
            return (
              <Typography.Link
                key={action.key}
                type={action.danger ? 'danger' : undefined}
                disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) action.onClick?.(record);
                }}
              >
                {action.label}
              </Typography.Link>
            );
          })}
        </Space>
      );
    },
    [rowActions, showConfirm],
  );

  // 操作列
  const actionColumn = useMemo(
    () => ({
      title: msg.actionColumnTitle,
      dataIndex: '_actions',
      key: '_actions',
      width: actionColumnWidth,
      fixed: actionColumnFixed === false ? undefined : actionColumnFixed,
      render: (_: unknown, record: T) => renderRowActions(record),
    }),
    [
      msg.actionColumnTitle,
      actionColumnWidth,
      actionColumnFixed,
      renderRowActions,
    ],
  );

  return {
    pagination,
    tableProps,
    toolBarRender,
    actionColumn,
    renderRowActions,
  };
}
